/**
 * Carga las fuentes que la página realmente usa.
 *
 * El editor guarda la tipografía por elemento como estilo inline dentro del HTML
 * del prop: `<span style="font-family:'Rubik', sans-serif">`. Hasta ahora el
 * sitio cargaba una lista FIJA de familias (`BUILDER_FONTS_HREF` en el layout),
 * así que el Builder solo podía ofrecer esas: cualquier otra se guardaba pero
 * se veía en el fallback.
 *
 * Esto recorre el árbol ya descomprimido, junta las familias que aparecen y las
 * carga bajo demanda. Con eso el editor puede ofrecer todo Google Fonts sin que
 * este repo tenga que conocer el catálogo.
 *
 * Es ADITIVO: la lista fija sigue cargándose igual, así que las páginas
 * existentes no dependen de esta detección para nada.
 *
 * Un <link> por familia y no un request combinado: un nombre de familia
 * inexistente devuelve 400 y, combinado, dejaría sin fuentes a todas las demás.
 * Aislado solo se rompe a sí mismo.
 */

/** `font-family:` seguido de la primera familia, con o sin comillas. */
const RE_FAMILIA = /font-family\s*:\s*(?:'([^']+)'|"([^"]+)"|([^;,"']+))/gi;

/** Genéricos y stacks del sistema: no son familias que haya que ir a buscar. */
const NO_ES_FAMILIA = new Set([
  'inherit', 'initial', 'unset', 'revert', 'sans-serif', 'serif', 'monospace',
  'cursive', 'fantasy', 'system-ui', 'ui-sans-serif', 'ui-serif', 'ui-monospace',
  'ui-rounded', 'emoji', 'math', 'fangsong', '-apple-system', 'blinkmacsystemfont',
]);

/** Nombre de familia plausible de Google Fonts: letras, números y espacios. */
const NOMBRE_VALIDO = /^[A-Za-z0-9][A-Za-z0-9 ]{0,49}$/;

/**
 * Junta las familias declaradas en los props de texto del árbol.
 *
 * Recorre valores string en vez de nodos concretos a propósito: la tipografía
 * puede estar en `title`, `subtitle`, `buttonText`, dentro de un item de un
 * array… y una lista de props conocidos se desactualiza sola.
 */
export function familiasDelArbol(estado: unknown): string[] {
  const encontradas = new Set<string>();
  const vistos = new Set<unknown>();

  const recorrer = (valor: unknown, profundidad: number) => {
    if (profundidad > 12 || valor == null) return;
    if (typeof valor === 'string') {
      if (!valor.includes('font-family')) return;
      RE_FAMILIA.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = RE_FAMILIA.exec(valor)) !== null) {
        const nombre = (m[1] ?? m[2] ?? m[3] ?? '').trim();
        if (!nombre || NO_ES_FAMILIA.has(nombre.toLowerCase())) continue;
        if (!NOMBRE_VALIDO.test(nombre)) continue;
        encontradas.add(nombre);
      }
      return;
    }
    if (typeof valor !== 'object') return;
    if (vistos.has(valor)) return;   // los árboles de Craft traen referencias cruzadas
    vistos.add(valor);
    for (const v of Object.values(valor as Record<string, unknown>)) {
      recorrer(v, profundidad + 1);
    }
  };

  recorrer(estado, 0);
  return [...encontradas];
}

/** Mismo id que usa ThemeProvider para la fuente global: no se duplican links. */
export const idDeFuente = (nombre: string): string =>
  `gfont-${nombre.replace(/\s+/g, '-')}`;

export const hrefDeFuente = (nombre: string): string =>
  `https://fonts.googleapis.com/css2?family=${nombre.replace(/ /g, '+')}:wght@400;700&display=swap`;

/** Agrega el <link> de cada familia que falte. Idempotente. */
export function cargarFuentes(nombres: string[]): void {
  if (typeof document === 'undefined') return;
  for (const nombre of nombres) {
    const id = idDeFuente(nombre);
    if (document.getElementById(id)) continue;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = hrefDeFuente(nombre);
    document.head.appendChild(link);
  }
}
